import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

// `||` not `??` — empty-string env must fall through to "" (same-origin relative paths).
// See PvaraRubricView.jsx for the longer rationale; audit F-26.
const API_URL = process.env.REACT_APP_API_URL || "";

const ACTION_PRETTY = {
  "update-application-status": "Status changed",
  "cv_download":                "CV opened",
  "prod_application_skipped":   "Source row skipped",
  "create-job":                 "Job created",
  "delete-job":                 "Job deleted",
  "update-job":                 "Job updated",
};

const ACTION_COLOR = {
  "update-application-status": "bg-blue-50  text-blue-800  border-blue-200",
  "cv_download":                "bg-purple-50 text-purple-800 border-purple-200",
  "prod_application_skipped":   "bg-amber-50  text-amber-800  border-amber-200",
  "create-job":                 "bg-green-50  text-green-800  border-green-200",
  "delete-job":                 "bg-red-50    text-red-800    border-red-200",
  "update-job":                 "bg-gray-50   text-gray-800   border-gray-200",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(iso); }
}

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)  return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function ActionPill({ action }) {
  const cls = ACTION_COLOR[action] || "bg-gray-100 text-gray-700 border-gray-200";
  return (
    <span className={"inline-block px-2 py-0.5 rounded text-[11px] border whitespace-nowrap " + cls}>
      {ACTION_PRETTY[action] || action}
    </span>
  );
}

// Compact one-line description for each row, derived from action + details.
function summary(log) {
  const a = log.action;
  const d = log.details || {};
  const userLabel = log.userLabel || (log.userInfo && (log.userInfo.fullName || log.userInfo.username)) || "system";
  switch (a) {
    case "update-application-status":
      return `${userLabel} set status from ${d.oldStatus || "—"} → ${d.newStatus || "—"} on application ${d.applicationId || ""}`;
    case "cv_download":
      return `${userLabel} opened CV ${d.filename || ""} (application ${d.applicationId || ""})`;
    case "prod_application_skipped":
      return `Ingest skipped ${d.name || "—"} (${d.cnic || "?"}) for ${d.role_id || "?"} — reason: ${d.reason || "?"}`;
    case "create-job":
    case "update-job":
    case "delete-job":
      return `${userLabel} ${a.replace("-", " ")} ${d.title || d.jobId || ""}`;
    default:
      return `${userLabel} performed ${a}`;
  }
}

export default function PvaraAuditLog() {
  const { user, apiFetch } = useAuth();
  const [logs, setLogs]       = useState([]);
  const [page, setPage]       = useState(1);
  const [pages, setPages]     = useState(1);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [actionFilter, setActionFilter] = useState("");
  const [actionList, setActionList]     = useState([]);
  const [usersById, setUsersById] = useState({});

  const PAGE_SIZE = 50;

  // Load a page of audit logs
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const q = new URLSearchParams({ page, limit: PAGE_SIZE });
        if (actionFilter) q.set("action", actionFilter);
        const res = await apiFetch(`${API_URL}/api/audit/?${q.toString()}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = await res.json();
        if (cancelled) return;
        setLogs(d.logs || []);
        setPages(d.pages || 1);
        setTotal(d.total || 0);
      } catch (e) {
        if (!cancelled && e.message !== "session-expired") setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user, page, actionFilter]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Load the action vocabulary once
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/audit/actions/list`);
        if (res.ok) {
          const d = await res.json();
          setActionList(d.actions || []);
        }
      } catch (_) { /* ignore */ }
    })();
  }, [user]);                       // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve user IDs → names (one batch lookup per page, cached)
  useEffect(() => {
    if (!user || logs.length === 0) return;
    const ids = Array.from(new Set(
      logs.map(l => l.user).filter(id => id && !usersById[id])
    ));
    if (ids.length === 0) return;
    (async () => {
      const next = { ...usersById };
      for (const id of ids) {
        try {
          const res = await apiFetch(`${API_URL}/api/users/${id}`);
          if (res.ok) {
            const d = await res.json();
            next[id] = (d.user && (d.user.fullName || d.user.username)) || (d.fullName || d.username) || id;
          } else {
            next[id] = id.slice(-6); // short stub when forbidden
          }
        } catch { next[id] = id.slice(-6); }
      }
      setUsersById(next);
    })();
  }, [logs]);                       // eslint-disable-line react-hooks/exhaustive-deps

  const enriched = useMemo(() => logs.map(l => ({
    ...l,
    userLabel: usersById[l.user] || (l.user ? l.user.slice(-6) : "system"),
  })), [logs, usersById]);

  if (!user) return null;

  return (
    <div className="space-y-4 max-w-full overflow-x-hidden">
      <div className="bg-white rounded-xl shadow p-4 sm:p-5 border border-gray-100">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Audit Log</h1>
        <p className="text-xs sm:text-sm text-gray-600 mt-1">
          Every status change, CV open, and ingest skip is recorded here.
          Showing <b>{total}</b> total events.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-gray-500">Filter by action:</span>
          <select
            value={actionFilter}
            onChange={e => { setActionFilter(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
          >
            <option value="">All actions</option>
            {actionList.map(a => (
              <option key={a} value={a}>{ACTION_PRETTY[a] || a}</option>
            ))}
          </select>
          {error && <span className="text-red-600">Error: {error}</span>}
          {loading && <span className="text-gray-500">Loading…</span>}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs text-gray-500 uppercase">
              <th className="px-4 py-2 whitespace-nowrap">When</th>
              <th className="px-4 py-2">Action</th>
              <th className="px-4 py-2">Detail</th>
            </tr>
          </thead>
          <tbody>
            {enriched.map((l, i) => (
              <tr key={l.id || l._id || i} className="border-t border-gray-100 hover:bg-gray-50 align-top">
                <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap">
                  <div>{fmtDate(l.timestamp)}</div>
                  <div className="text-[10px] text-gray-400">{relTime(l.timestamp)}</div>
                </td>
                <td className="px-4 py-2"><ActionPill action={l.action} /></td>
                <td className="px-4 py-2 text-xs text-gray-700">{summary(l)}</td>
              </tr>
            ))}
            {!loading && enriched.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-500">No audit events match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-xs">
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            className="px-3 py-1.5 border border-gray-300 rounded-md bg-white disabled:opacity-50 hover:bg-gray-50"
          >← prev</button>
          <span>page <b>{page}</b> of {pages}</span>
          <button
            disabled={page >= pages}
            onClick={() => setPage(p => Math.min(pages, p + 1))}
            className="px-3 py-1.5 border border-gray-300 rounded-md bg-white disabled:opacity-50 hover:bg-gray-50"
          >next →</button>
        </div>
      )}
    </div>
  );
}
